/**
 * Responsibility: one discovered card in full.
 *
 * Identical in frame, size and behaviour to the search dialog, because both
 * answer the same questions about the same kind of thing. Everything about
 * the box comes from `CardDialogShell`; this file supplies the tabs and the
 * figures that are particular to a discovery row.
 *
 * The dialog does not scroll. A hundred sales or forty listings scroll
 * inside their own region, which is what the tabs exist for.
 */
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { useCardDetail } from '../../api/hooks/useDiscovery';
import type { DiscoveredCard } from '../../api/client';
import { usePageState } from '../../hooks/usePageState';
import { CardDialogShell, ScrollRegion } from '../cards/CardDialogShell';
import { CardSalesTab } from '../cards/CardSalesTab';
import { CardStats } from './CardStats';
import { ImageLightbox } from './ImageLightbox';
import { ListingsList } from './ListingsList';

type CardDetailDialogProps = {
  card: DiscoveredCard | null;
  onClose: () => void;
  onArchive: (card: DiscoveredCard) => void;
};

type Zoom = { src: string | null; alt: string };

export function CardDetailDialog({ card, onClose, onArchive }: CardDetailDialogProps) {
  const [tab, setTab] = usePageState('discovery.dialog.tab', 0);
  const [zoom, setZoom] = usePageState<Zoom | null>('discovery.dialog.zoom', null);
  const detail = useCardDetail(card?.discovery_key ?? null);

  if (!card) return null;

  const data = detail.data;
  const rawCount = data?.raw_listings?.length ?? 0;
  const slabCount = data?.graded_listings?.length ?? 0;
  const saleCount = data?.market_sales?.length ?? 0;

  return (
    <>
      <CardDialogShell
        open
        tab={tab}
        onTab={setTab}
        onClose={onClose}
        title={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <span>{card.card_name}</span>
            <Chip size="small" variant="outlined" label={card.set_name} />
            {card.set_year ? <Chip size="small" variant="outlined" label={card.set_year} /> : null}
            {card.variant && card.variant !== 'Base' ? (
              <Chip size="small" variant="outlined" label={card.variant} />
            ) : null}
          </Stack>
        }
        tabs={[
          {
            label: 'Figures',
            content: (
              <CardStats
                card={card}
                detail={data}
                loading={detail.isLoading}
                onZoom={(src, alt) => setZoom({ src: src ?? null, alt })}
              />
            ),
          },
          {
            label: saleCount > 0 ? `Sales (${saleCount})` : 'Sales',
            content: (
              <CardSalesTab
                grades={data?.market_grades ?? []}
                sales={data?.market_sales ?? []}
                sourceUrl={data?.market_url ?? null}
                loading={detail.isLoading}
                // This board asks one question - what if a copy comes
                // back a 10 - so the sales open on 10.
                preferredGrade="PSA 10"
              />
            ),
          },
          {
            label: rawCount > 0 ? `Raw cards (${rawCount})` : 'Raw cards',
            content: (
              <ScrollRegion>
                <ListingsList
                  listings={data?.raw_listings}
                  loading={detail.isLoading}
                  emptyMessage="No raw copies listed right now. Without eBay credentials configured this stays empty."
                />
              </ScrollRegion>
            ),
          },
          {
            label: slabCount > 0 ? `Slabs (${slabCount})` : 'Slabs',
            content: (
              <ScrollRegion>
                <ListingsList
                  listings={data?.graded_listings}
                  loading={detail.isLoading}
                  gradeFilter
                  emptyMessage="No graded copies listed right now. Without eBay credentials configured this stays empty."
                />
              </ScrollRegion>
            ),
          },
        ]}
        actions={
          <>
            <Button size="small" color="error" onClick={() => onArchive(card)}>
              Not interested
            </Button>
            <Button size="small" variant="contained" onClick={onClose}>
              Close
            </Button>
          </>
        }
      />

      {zoom ? (
        <ImageLightbox src={zoom.src} alt={zoom.alt} onClose={() => setZoom(null)} />
      ) : null}
    </>
  );
}
